// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import {
  RecordPaymentInput,
  recordPayment,
} from '@/billing/api/record-payment';
import { invalidateInvoiceQueries } from '@/billing/hooks/invalidate-invoice-queries';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// Codes that are user-facing validation errors the caller handles explicitly
// (field-level setError, known-error snackbar, etc.). Skip Sentry for these —
// Sentry is reserved for unexpected failures.
const EXPECTED_ERROR_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_not_payable,
  ERROR_CODE_MAPPING.invoice_customer_not_found,
  ERROR_CODE_MAPPING.payment_exceeds_balance,
  ERROR_CODE_MAPPING.invoice_mixed_currency,
  ERROR_CODE_MAPPING.currency_missing_iso,
  ERROR_CODE_MAPPING.currency_invalid_amount,
  ERROR_CODE_MAPPING.currency_sub_cent_precision,
  ERROR_CODE_MAPPING.currency_value_exceeds_maximum,
]);

interface UseRecordPaymentOptions {
  invoiceKey: string;
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

export function useRecordPayment(options: UseRecordPaymentOptions) {
  const queryClient = useQueryClient();

  const invalidateAll = () =>
    invalidateInvoiceQueries({
      queryClient,
      invoiceKey: options.invoiceKey,
    });

  return useMutation({
    mutationFn: (input: RecordPaymentInput) => recordPayment(input),
    onSuccess: async () => {
      await invalidateAll();
      options.onSuccess?.();
    },
    onError: async (error: AxiosError) => {
      const apiError = handleAPIError(error);
      await invalidateAll();
      if (
        !apiError.sentToSentry &&
        !EXPECTED_ERROR_CODES.has(apiError.apiStatusCode)
      ) {
        Sentry.captureException(error);
      }
      options.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode,
        apiError.httpStatusCode
      );
    },
  });
}
